/**
 * Developer Settings Section
 * 
 * Allows users to manage API tokens for programmatic access to de.iterate APIs.
 * Supports full access or granular permissions per resource.
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash, 
  faCopy, 
  faCheck, 
  faKey,
  faXmark,
  faExclamationTriangle,
  faSpinner
} from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useTenancy } from '@/contexts/TenancyContext';
import { useAuth } from '@/contexts/AuthContext';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

interface APITokenPermission {
  resource: string;
  actions: ('read' | 'write' | 'delete')[];
}

interface APIToken {
  id: string;
  name: string;
  tokenPrefix: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  isActive: boolean;
  accessType: 'full' | 'granular';
  permissions: APITokenPermission[];
  description?: string;
}

interface APIResource {
  id: string;
  name: string;
  description?: string;
  actions: string[];
}

const EXPIRY_OPTIONS = [
  { value: 'never', label: 'Never expires' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '1y', label: '1 year' },
];

function getExpiryDate(option: string): string | null {
  if (option === 'never') return null;
  const now = new Date();
  switch (option) {
    case '7d':
      now.setDate(now.getDate() + 7);
      break;
    case '30d':
      now.setDate(now.getDate() + 30);
      break;
    case '90d':
      now.setDate(now.getDate() + 90);
      break;
    case '1y':
      now.setFullYear(now.getFullYear() + 1);
      break;
  }
  return now.toISOString();
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

export default function DeveloperSection() {
  const { user, isAuthReady } = useAuth();
  const { activeTenancy } = useTenancy();
  const [tokens, setTokens] = useState<APIToken[]>([]);
  const [resources, setResources] = useState<APIResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState<APIToken | null>(null);
  const [newTokenDialogOpen, setNewTokenDialogOpen] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formExpiry, setFormExpiry] = useState('30d');
  const [formAccessType, setFormAccessType] = useState<'full' | 'granular'>('full');
  const [formPermissions, setFormPermissions] = useState<Record<string, ('read' | 'write' | 'delete')[]>>({});
  const [creating, setCreating] = useState(false);

  const tenancyId = activeTenancy?.id;

  useEffect(() => {
    if (!activeTenancy?.id || ['Loading', 'Loading...', ''].includes(activeTenancy?.id)) return;
    // Wait for auth to be ready and user to be logged in before loading data
    if (isAuthReady && user && tenancyId) {
      // Load sequentially to avoid race condition with token fetching
      (async () => {
        await loadResources();
        await loadTokens();
      })();
    }
  }, [isAuthReady, user, tenancyId]);

  async function loadTokens() {
    if (!tenancyId) return;
    
    setLoading(true);
    try {
      const data = await deiterateAPI.GET<{ tokens: APIToken[] }>(
        '/v1/api-tokens',
        { 
          backend: 'thalassa',
          headers: { 'X-Tenancy-Id': tenancyId },
        }
      );
      setTokens(data.tokens || []);
    } catch (error) {
      console.error('Error loading tokens:', error);
      toast.error('Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  }

  async function loadResources() {
    try {
      const data = await deiterateAPI.GET<{ resources: APIResource[] }>(
        '/v1/api-tokens/resources',
        { backend: 'thalassa' }
      );
      setResources(data.resources || []);
    } catch (error) {
      console.error('Error loading resources:', error);
    }
  }

  async function handleCreateToken() {
    if (!formName.trim()) {
      toast.error('Please enter a token name');
      return;
    }

    if (formAccessType === 'granular' && Object.keys(formPermissions).length === 0) {
      toast.error('Please select at least one permission for granular access');
      return;
    }

    setCreating(true);
    try {
      const permissions = formAccessType === 'granular' 
        ? Object.entries(formPermissions)
            .filter(([_, actions]) => actions.length > 0)
            .map(([resource, actions]) => ({ resource, actions }))
        : [];

      const data = await deiterateAPI.POST<{ token: string; details: any }>(
        '/v1/api-tokens',
        {
          backend: 'thalassa',
          headers: { 'X-Tenancy-Id': tenancyId! },
          body: JSON.stringify({
            name: formName,
            description: formDescription || undefined,
            expiresAt: getExpiryDate(formExpiry),
            accessType: formAccessType,
            permissions,
          }),
        }
      );

      setNewToken(data.token);
      setCreateDialogOpen(false);
      setNewTokenDialogOpen(true);
      
      // Reset form
      setFormName('');
      setFormDescription('');
      setFormExpiry('30d');
      setFormAccessType('full');
      setFormPermissions({});
      
      // Reload tokens
      loadTokens();
    } catch (error) {
      console.error('Error creating token:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create token');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevokeToken() {
    if (!tokenToDelete || !tenancyId) return;

    try {
      await deiterateAPI.DELETE(
        `/v1/api-tokens/${tokenToDelete.id}`,
        {
          backend: 'thalassa',
          headers: { 'X-Tenancy-Id': tenancyId },
        }
      );

      toast.success('Token revoked successfully');
      setDeleteDialogOpen(false);
      setTokenToDelete(null);
      loadTokens();
    } catch (error) {
      console.error('Error revoking token:', error);
      toast.error('Failed to revoke token');
    }
  }

  async function handleToggleToken(token: APIToken) {
    if (!tenancyId) return;

    try {
      await deiterateAPI.PATCH(
        `/v1/api-tokens/${token.id}`,
        {
          backend: 'thalassa',
          headers: { 'X-Tenancy-Id': tenancyId },
          body: JSON.stringify({
            isActive: !token.isActive,
          }),
        }
      );

      toast.success(`Token ${token.isActive ? 'disabled' : 'enabled'}`);
      loadTokens();
    } catch (error) {
      console.error('Error updating token:', error);
      toast.error('Failed to update token');
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Token copied to clipboard');
  }

  function togglePermissionAction(resource: string, action: 'read' | 'write' | 'delete') {
    setFormPermissions(prev => {
      const current = prev[resource] || [];
      const updated = current.includes(action)
        ? current.filter(a => a !== action)
        : [...current, action];
      
      if (updated.length === 0) {
        const { [resource]: _, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [resource]: updated };
    });
  }

  if (!tenancyId) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Please select a company to manage API tokens.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">API Tokens</h2>
        <p className="text-muted-foreground mt-1">
          Create and manage API tokens for programmatic access to de.iterate APIs.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Your API Tokens</CardTitle>
            <CardDescription>
              Tokens allow external applications to access your data via the API.
            </CardDescription>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-4 w-4" />
            Create Token
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tokens.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FontAwesomeIcon icon={faKey} className="h-12 w-12 mb-4 opacity-50" />
              <p>No API tokens yet.</p>
              <p className="text-sm">Create a token to get started with the API.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tokens.map((token) => (
                <div
                  key={token.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    !token.isActive || isExpired(token.expiresAt)
                      ? 'bg-muted/50 border-muted'
                      : 'bg-card border-border'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{token.name}</span>
                      {token.accessType === 'full' ? (
                        <Badge variant="default">Full Access</Badge>
                      ) : (
                        <Badge variant="secondary">Granular</Badge>
                      )}
                      {!token.isActive && (
                        <Badge variant="destructive">Disabled</Badge>
                      )}
                      {isExpired(token.expiresAt) && (
                        <Badge variant="destructive">Expired</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <code className="bg-muted px-2 py-0.5 rounded">{token.tokenPrefix}</code>
                      <span>Created: {formatDate(token.createdAt)}</span>
                      <span>Expires: {formatDate(token.expiresAt)}</span>
                      {token.lastUsedAt && (
                        <span>Last used: {formatDate(token.lastUsedAt)}</span>
                      )}
                    </div>
                    {token.description && (
                      <p className="text-sm text-muted-foreground mt-1">{token.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Switch
                      checked={token.isActive}
                      onCheckedChange={() => handleToggleToken(token)}
                      disabled={isExpired(token.expiresAt)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setTokenToDelete(token);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Token Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent 
          className="max-w-6xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>

            {/* Header */}
            <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                <FontAwesomeIcon icon={faKey} className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Create API Token</h2>
              <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                Create a new API token for programmatic access to your data.
              </p>
            </div>

            {/* Form Content - scrollable */}
            <div className="p-6 flex-1 min-h-0 overflow-y-auto space-y-4">
              {/* Two column grid for basic fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Token Name */}
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-4 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <Label htmlFor="name" className="block text-sm font-semibold text-foreground mb-1">
                    Token Name <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">A descriptive name for this token</p>
                  <Input
                    id="name"
                    placeholder="e.g., Production Integration"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    className="bg-white/5 border border-primary/10 h-10 focus:border-primary/50"
                  />
                </div>

                {/* Expiration */}
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-4 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <Label htmlFor="expiry" className="block text-sm font-semibold text-foreground mb-1">
                    Expiration
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">When should this token expire?</p>
                  <Select value={formExpiry} onValueChange={setFormExpiry}>
                    <SelectTrigger className="bg-white/5 border-primary/10 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description - full width */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-4 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <Label htmlFor="description" className="block text-sm font-semibold text-foreground mb-1">
                  Description
                </Label>
                <p className="text-xs text-muted-foreground mb-2">What is this token used for?</p>
                <Input
                  id="description"
                  placeholder="Optional description..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="bg-white/5 border border-primary/10 h-10 focus:border-primary/50"
                />
              </div>

              {/* Access Type */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-4 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <Label className="block text-sm font-semibold text-foreground mb-1">Access Type</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  {formAccessType === 'full'
                    ? 'Token will have access to all API resources.'
                    : 'Select specific resources and actions the token can access.'}
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={formAccessType === 'full' ? 'default' : 'outline'}
                    onClick={() => setFormAccessType('full')}
                    className="flex-1"
                  >
                    Full Access
                  </Button>
                  <Button
                    type="button"
                    variant={formAccessType === 'granular' ? 'default' : 'outline'}
                    onClick={() => setFormAccessType('granular')}
                    className="flex-1"
                  >
                    Granular
                  </Button>
                </div>
              </div>

              {/* Permissions (Granular) - Redesigned */}
              {formAccessType === 'granular' && (
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="block text-sm font-semibold text-foreground">Permissions</Label>
                      <p className="text-xs text-muted-foreground mt-1">Configure access for each resource</p>
                    </div>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Read</span>
                      <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-500 border border-blue-500/20">Write</span>
                      <span className="px-2 py-1 rounded bg-red-500/10 text-red-500 border border-red-500/20">Delete</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {resources.map((resource) => {
                      const perms = formPermissions[resource.id] || [];
                      const hasAny = perms.length > 0;
                      return (
                        <div 
                          key={resource.id} 
                          className={`rounded-lg border p-4 transition-all ${
                            hasAny 
                              ? 'border-primary/30 bg-primary/5' 
                              : 'border-primary/10 bg-white/5 hover:border-primary/20'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="min-w-0">
                              <span className="font-medium text-foreground block">{resource.name}</span>
                              {resource.description && (
                                <span className="text-xs text-muted-foreground line-clamp-1">{resource.description}</span>
                              )}
                            </div>
                            {hasAny && (
                              <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full shrink-0">
                                {perms.length}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {(['read', 'write', 'delete'] as const).map((action) => {
                              const isSelected = perms.includes(action);
                              const colorClass = action === 'read' 
                                ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-500' 
                                : action === 'write'
                                  ? 'bg-blue-500/20 border-blue-500/40 text-blue-500'
                                  : 'bg-red-500/20 border-red-500/40 text-red-500';
                              return (
                                <button
                                  key={action}
                                  type="button"
                                  onClick={() => togglePermissionAction(resource.id, action)}
                                  className={`flex-1 py-2 px-3 rounded-md text-xs font-medium capitalize transition-all border ${
                                    isSelected 
                                      ? colorClass
                                      : 'bg-white/5 border-white/10 text-muted-foreground hover:bg-white/10'
                                  }`}
                                >
                                  {action}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-card flex justify-between items-center shrink-0">
              <p className="text-xs text-muted-foreground">
                {!formName.trim() ? 'Enter a token name to continue' : 'Ready to create token'}
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-white/10">
                  Cancel
                </Button>
                <Button onClick={handleCreateToken} disabled={creating || !formName.trim()}>
                  {creating && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />}
                  Create Token
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Token Display Dialog */}
      <Dialog open={newTokenDialogOpen} onOpenChange={setNewTokenDialogOpen}>
        <DialogContent 
          className="max-w-lg bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Header */}
            <div className="p-8 pb-6 border-b border-white/10 bg-card text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-amber-500/10 to-amber-500/30 mb-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Copy Your API Token</h2>
              <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
                This is the only time you'll see this token. Copy it now and store it securely.
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                <div className="flex items-center gap-3">
                  <code className="flex-1 p-3 bg-white/5 rounded-lg font-mono text-sm break-all border border-primary/10">
                    {newToken}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => newToken && copyToClipboard(newToken)}
                    className="shrink-0 border-primary/10 hover:bg-white/10"
                  >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="h-4 w-4" />
                  </Button>
                </div>
                {copied && (
                  <p className="text-xs text-emerald-500 mt-2 flex items-center gap-1">
                    <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                    Copied to clipboard
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-card flex justify-end">
              <Button onClick={() => setNewTokenDialogOpen(false)}>
                I've Copied the Token
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Header */}
            <div className="p-8 pb-6 border-b border-white/10 bg-card text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-destructive/10 to-destructive/30 mb-4">
                <FontAwesomeIcon icon={faTrash} className="w-8 h-8 text-destructive" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Revoke API Token?</h2>
              <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
                This will permanently revoke the token "{tokenToDelete?.name}". Any applications using this token will no longer be able to access the API.
              </p>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-card flex justify-end gap-3">
              <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleRevokeToken} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Revoke Token
              </AlertDialogAction>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
